import React, { useState, useRef, useEffect } from "react";
import { useVenues } from "../../hooks/use-venues";
import {
  Venue,
  VenueType,
  CreateVenueInput,
  VENUE_TYPE_LABELS,
} from "../../types/asset-types";

export interface VenueSelectorProps {
  selectedVenue?: Venue;
  onVenueSelect: (venue: Venue) => void;
  onVenueCreate?: (venue: Venue) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
}

export function VenueSelector({
  selectedVenue,
  onVenueSelect,
  onVenueCreate,
  disabled,
  error,
  required,
}: VenueSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchResults, setSearchResults] = useState<Venue[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { venues, searchVenues, createVenue } = useVenues({
    autoFetch: false,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowCreateForm(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search venues when query changes
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchQuery.trim().length > 0) {
        setIsSearching(true);
        try {
          const results = await searchVenues(searchQuery.trim());
          setSearchResults(results);
        } catch (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery, searchVenues]);

  const handleInputClick = () => {
    setIsOpen(true);
    setShowCreateForm(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setIsOpen(true);
    setShowCreateForm(false);
  };

  const handleVenueSelect = (venue: Venue) => {
    onVenueSelect(venue);
    setIsOpen(false);
    setSearchQuery("");
    setShowCreateForm(false);
  };

  const handleCreateNew = () => {
    setShowCreateForm(true);
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="venue-selector">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Venue {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={selectedVenue ? selectedVenue.name : searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          onClick={handleInputClick}
          placeholder="Search venues or create new..."
          disabled={disabled}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          data-testid="venue-input"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <svg
            className="h-4 w-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid="venue-dropdown"
        >
          {showCreateForm ? (
            <VenueQuickCreateForm
              onVenueCreate={(venue) => {
                handleVenueSelect(venue);
                onVenueCreate?.(venue);
              }}
              onCancel={() => setShowCreateForm(false)}
              initialName={searchQuery}
            />
          ) : (
            <>
              {/* Search Results */}
              {isSearching && (
                <div
                  className="px-3 py-2 text-sm text-gray-500"
                  data-testid="venue-searching"
                >
                  Searching...
                </div>
              )}

              {!isSearching && searchResults.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-200">
                    Search Results
                  </div>
                  {searchResults.map((venue) => (
                    <VenueOption
                      key={venue.id}
                      venue={venue}
                      onClick={() => handleVenueSelect(venue)}
                    />
                  ))}
                </>
              )}

              {!isSearching && searchQuery && searchResults.length === 0 && (
                <div
                  className="px-3 py-2 text-sm text-gray-500"
                  data-testid="venue-no-results"
                >
                  No venues found for "{searchQuery}"
                </div>
              )}

              {/* Create New Option */}
              <button
                onClick={handleCreateNew}
                className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-200 flex items-center"
                data-testid="venue-create-new"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Create new venue
                {searchQuery && ` "${searchQuery}"`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function VenueOption({
  venue,
  onClick,
}: {
  venue: Venue;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
      data-testid={`venue-option-${venue.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-900">{venue.name}</div>
          <div className="text-xs text-gray-500">
            {VENUE_TYPE_LABELS[venue.type]} • {venue.city}, {venue.country}
          </div>
        </div>
        <div className="text-xs text-gray-400">{venue.address}</div>
      </div>
    </button>
  );
}

function VenueQuickCreateForm({
  onVenueCreate,
  onCancel,
  initialName,
}: {
  onVenueCreate: (venue: Venue) => void;
  onCancel: () => void;
  initialName?: string;
}) {
  const [formData, setFormData] = useState<CreateVenueInput>({
    name: initialName || "",
    type: VenueType.OTHER,
    address: "",
    city: "",
    country: "US",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const { createVenue } = useVenues({ autoFetch: false });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(undefined);

    try {
      const venue = await createVenue(formData);
      onVenueCreate(venue);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create venue");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="p-4 border-b border-gray-200"
      data-testid="venue-quick-create-form"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-900">Create New Venue</h4>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
          data-testid="venue-quick-create-close"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

      <div className="space-y-3">
        <div>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Venue name"
            required
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            data-testid="venue-quick-name"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <select
            value={formData.type}
            onChange={(e) =>
              setFormData({ ...formData, type: e.target.value as VenueType })
            }
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            data-testid="venue-quick-type"
          >
            {Object.entries(VENUE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={formData.country}
            onChange={(e) =>
              setFormData({ ...formData, country: e.target.value })
            }
            placeholder="Country"
            required
            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            data-testid="venue-quick-country"
          />
        </div>

        <div>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            placeholder="City"
            required
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            data-testid="venue-quick-city"
          />
        </div>

        <div>
          <input
            type="text"
            value={formData.address}
            onChange={(e) =>
              setFormData({ ...formData, address: e.target.value })
            }
            placeholder="Address"
            required
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            data-testid="venue-quick-address"
          />
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
            data-testid="venue-quick-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            data-testid="venue-quick-submit"
          >
            {isSubmitting ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </form>
  );
}
